/**
 * The drawer recipe of the product, which is the Ledger Sheet: the surface
 * every drawer is drawn with, and the widths a drawer can ask for by name.
 *
 * The surface is the Ledger's (opaque `--color-surface`, 1px border, radius 6,
 * shadow-3, no blur or translucency: DEVIATIONS.md D-08, L-05). The widths the
 * registry's drawers ask for (`xl`, `eval` 1024px, `span` 70%, …) stay: they
 * hold feature content laid out for them. The Ledger's own sheet sizes are
 * added beside them: `inspector` (right, 380), `navigation` (left, 280) and,
 * by placement, the bottom sheet at 85% of the viewport.
 *
 * A drawer sets its width with `size`, never with a maxWidth of its own, so the
 * widths of the product stay a short list a reader can hold in mind. Add a step
 * here when a surface needs one; do not override the width at the call site.
 *
 * The steps this file does not name fall through to Chakra's own drawer recipe,
 * which is where `sm`, `md` and `lg` come from.
 *
 * @see dev/docs/best_practices/drawers.md
 */

import { defineSlotRecipe } from "@chakra-ui/react";

/**
 * The scenario editor of Agent Testing.
 *
 * It sits between Chakra's `md` (32rem) and `lg` (42rem). The editor asks four
 * questions and holds a criteria list beside them, which `md` cut short, while
 * `lg` made a form of four fields read as half a page.
 */
export const DRAWER_SIZE_2XL_MAX_WIDTH = "38.5rem";

export const drawerSlotRecipe = defineSlotRecipe({
  slots: [
    "backdrop",
    "positioner",
    "content",
    "header",
    "title",
    "body",
    "footer",
    "closeTrigger",
  ],
  base: {
    // Sheets sit in the layer manager's page band (src/theme/layerStack.ts).
    backdrop: {
      "--drawer-z-index": "zIndex.modal",
      background: "var(--color-scrim)",
      backdropFilter: "none",
    },
    positioner: {
      "--drawer-z-index": "zIndex.modal",
    },
    content: {
      "--drawer-z-index": "zIndex.modal",
      maxWidth: "70%",
      // `bg`, the key Chakra's own recipe uses, so this replaces its paint.
      bg: "var(--color-surface)",
      color: "var(--color-fg)",
      backdropFilter: "none",
      border: "1px solid",
      borderColor: "var(--color-border)",
      borderRadius: "var(--radius-4)",
      boxShadow: "var(--shadow-3)",
      fontSize: "var(--text-13)",
      textStyle: "none",
      _open: {
        animationDuration: "var(--dur-3)",
        animationTimingFunction: "var(--ease-out)",
      },
      _closed: {
        animationDuration: "var(--dur-3)",
        animationTimingFunction: "var(--ease-in)",
      },
    },
    header: {
      paddingY: "var(--space-4)",
      paddingInlineStart: "var(--space-5)",
      // Room for the close button, which is placed absolutely.
      paddingInlineEnd: "calc(var(--space-5) + var(--hit-min))",
    },
    title: {
      fontSize: "var(--text-16)",
      fontWeight: "var(--weight-semibold)",
      lineHeight: "var(--leading-tight)",
      textStyle: "none",
    },
    body: {
      paddingInline: "var(--space-5)",
    },
    footer: {
      gap: "var(--space-2)",
      paddingInline: "var(--space-5)",
      paddingTop: "var(--space-3)",
      paddingBottom: "var(--space-4)",
    },
    closeTrigger: {
      top: "var(--space-3)",
      insetEnd: "var(--space-3)",
    },
  },
  variants: {
    size: {
      span: { content: { maxWidth: "70%" } },
      full: { content: { maxWidth: "100%" } },
      eval: { content: { maxWidth: "1024px" } },
      "2xl": { content: { maxWidth: DRAWER_SIZE_2XL_MAX_WIDTH } },
      xl: { content: { maxWidth: "4xl" } },
      // The Ledger Sheet sizes: an inspector from the right, the phone
      // navigation from the left, a form from the bottom.
      inspector: {
        content: { maxWidth: "min(var(--layout-inspector-overlay-w), 100%)" },
      },
      navigation: {
        content: { maxWidth: "min(calc(var(--space-8) * 7), 100%)" },
      },
    },
    placement: {
      bottom: {
        content: {
          maxWidth: "100%",
          height: "85dvh",
          maxHeight: "85dvh",
          borderBottomRadius: "0",
        },
      },
    },
  },
  defaultVariants: {
    size: "xl",
  },
});
